import React, {useContext, useEffect, useState} from "react";
import PropTypes from "prop-types";
import {makeStyles, Link, Typography, Box} from "@material-ui/core";
import {useCookies} from "react-cookie";
import clsx from "clsx";
import {useI18N, useLocalizedUrl, useLocation} from "../../../common/common";
import {useKeycloakFunctions, useUserAccounts} from "../../../hooks";
import {useAnalytics} from "../../../store/providers/AnalyticsProvider";
import {COOKIE_TRIAL_REGISTRATION} from "../../../common/constant";
import {GENERAL_TERMS_OF_USE, IMPRINT, MARKET_PLACE_BUY, MEMBERSHIP, TRY_OUT_HAPIMAG} from "../../../common/routes";
import {CustomButton, ResponsiveDialog} from "@hapinest/theme";
import ActionCardsDialog from "../../widget/dialog/ActionCardsDialog";
import ActionCard from "../../widget/card/ActionCard";
import HapimagLogo from "../../widget/logo/HapimagLogo";
import {LocaleContext} from "../../../store/providers/LocaleProvider";
import {LANGUAGE} from "../../../common/i18n/locales/localeConstant";

const useStyles = makeStyles((theme) => ({
    hyperlink: {
        color: theme.palette.primary.main,
        textDecoration: "underline",
        cursor: "pointer"
    },
    paddingLeftHalf: {
        paddingLeft: theme.spacing(0.4)
    },
    inlineBlock: {
        display: "inline-block"
    },
    alert: {
        maxWidth: "625px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    },
    displayInlineBlock: {
        display: "inline-block"
    },
}));

const LoginTrialOrMemberDialog = ({isOpen, onClose, showFullMembershipCardProspect}) => {

    const classes = useStyles();
    const {i18n} = useI18N();
    const {sendCustomEvent, eventNames, userTypes} = useAnalytics();
    const {login, register, authenticated, isProspect} = useKeycloakFunctions();
    const [cookies, setCookie] = useCookies([COOKIE_TRIAL_REGISTRATION.name]);
    const {navigate, getComposePagePath} = useLocation();
    const {getLocalizedUrl} = useLocalizedUrl();
    const {
        convertTrial,
        convertUserToTrial,
        loadingConvertTrial,
        errorConvertTrial
    } = useUserAccounts();
    const {locale} = useContext(LocaleContext)[0];

    const [prospectMember] = useState(isProspect());
    const [introduceMembership, setIntroduceMembership] = useState();

    useEffect(() => {
        if (authenticated) {
            if(prospectMember) {
                setIntroduceMembership(true);
            } else {
                setIntroduceMembership(false);
            }
        } else {
            setIntroduceMembership(true);
        }
    }, [authenticated]);

    useEffect(() => {
        if (convertTrial) {
            document.location.reload();
        }
    }, [convertTrial]);

    const handleRegisterTrial = () => {
        if(!cookies[COOKIE_TRIAL_REGISTRATION.name]) {
            setCookie(COOKIE_TRIAL_REGISTRATION.name, true, COOKIE_TRIAL_REGISTRATION.options);
        }
        sendCustomEvent({
            event_name: eventNames.SIGN_UP_START,
            user_type: userTypes.TRIAL
        });
        register(window.location.href);
    };

    const handleLinkClick = (route) => {
        const localisedUrl = getLocalizedUrl(route);
        window.open(localisedUrl, "_blank");
    };

    const handleGoTrialClick = () => {
        convertUserToTrial().then((result) => {
            if (result) {
                sendCustomEvent({
                    event_name: eventNames.UPGRADE_TO_TRIAL,
                    user_type: userTypes.TRIAL
                });
            }
        });
    };

    const tryOutDescription = (
        <>
            {i18n("view.auth.trial_travel_three_times")} &nbsp;
            <Link
                href={getComposePagePath(TRY_OUT_HAPIMAG)}
                rel="noopener noreferrer"
            >
                <Typography
                    variant={"body2"}
                    className={classes.displayInlineBlock}
                >
                    {i18n("view.resort.good_to_know.read_more")}
                </Typography>
            </Link>
        </>
    );

    const loginOrTrialRegisterCards = [
        <ActionCard
            key={1}
            image="/images/illu_compi.svg"
            title={i18n("view.auth.title.welcome_back")}
            description={
                <Box
                    p={2}
                >
                    <Typography
                        component={"div"}
                        variant={"body1"}
                        align={"center"}
                    >
                        {i18n("view.auth.login_for_new_adventures")}
                    </Typography>
                </Box>
            }
            cta={
                <CustomButton
                    variant={"contained"}
                    fullWidth
                    disableElevation
                    onClick={login}
                >
                    {i18n("common.label.login")}
                </CustomButton>
            }
        />,
        <ActionCard
            key={2}
            image="/images/try-out-hapimag.svg"
            title={i18n("view.booking.confirmation_success.try_out_hapimag")}
            description={
                <Box
                    p={2}
                >
                    <Typography
                        component={"div"}
                        variant={"body1"}
                        align={"center"}
                    >
                        {tryOutDescription}
                    </Typography>
                </Box>
            }
            cta={
                <CustomButton
                    variant={"contained"}
                    fullWidth
                    disableElevation
                    onClick={handleRegisterTrial}
                >
                    {i18n("view.auth.register_as_trial")}
                </CustomButton>
            }
        />
    ];

    const membershipCards = [
        <ActionCard
            key={1}
            image="/images/become_trial.svg"
            title={i18n("dialog.trial_membership.become_trial.membership")}
            description={
                <Box
                    p={2}
                >
                    <Typography
                        component={"div"}
                        variant={"body1"}
                        align={"center"}
                    >
                        {i18n("dialog.trial_membership.become_trial.membership_txt_1")}
                        <br/>
                        <Typography
                            variant="body1"
                            className={clsx(classes.hyperlink, classes.paddingLeftHalf)}
                            onClick={() => handleLinkClick(getComposePagePath(TRY_OUT_HAPIMAG))}

                        >
                            {i18n("dialog.trial_membership.become_trial.membership_txt_1_link")}
                        </Typography>
                        <br/>
                        <br/>
                        <>
                            {i18n("dialog.trial_membership.become_trial.membership_txt_2")}
                            <Typography
                                variant="body1"
                                className={clsx(classes.hyperlink, classes.paddingLeftHalf, classes.inlineBlock)}
                                onClick={() => handleLinkClick(IMPRINT + GENERAL_TERMS_OF_USE)}

                            >
                                {i18n("layout.footer.terms").toLowerCase()}
                            </Typography>
                        </>
                    </Typography>
                </Box>
            }
            cta={
                <CustomButton
                    variant={"contained"}
                    fullWidth
                    disableElevation
                    loading={loadingConvertTrial}
                    onClick={handleGoTrialClick}
                >
                    {i18n("dialog.trial_membership.become_trial.membership_cta")}
                </CustomButton>
            }
        />,
        <>
            {showFullMembershipCardProspect && (
                <ActionCard
                    key={2}
                    image="/images/shareholder.svg"
                    title={i18n("dialog.trial_membership.become_trial.shareholder")}
                    description={
                        <Box
                            p={2}
                        >
                            <Typography
                                component={"div"}
                                variant={"body1"}
                                align={"center"}
                            >
                                {i18n("dialog.trial_membership.become_trial.shareholder_txt_1")}
                                <Typography
                                    variant="body1"
                                    className={clsx(classes.hyperlink, classes.paddingLeftHalf, classes.inlineBlock)}
                                    onClick={() => handleLinkClick(getComposePagePath(MEMBERSHIP))}

                                >
                                    {i18n("dialog.trial_membership.become_trial.shareholder_txt_1_link")}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    className={classes.inlineBlock}

                                >
                                    {i18n("dialog.trial_membership.become_trial.shareholder_txt_2")}
                                </Typography>
                            </Typography>

                            {locale === LANGUAGE.EN && (
                                <Box mt={4}>
                                    <Typography variant="caption">
                                        {i18n("common.label.uk_disclaimer")}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    }
                    cta={
                        <CustomButton
                            variant={"contained"}
                            fullWidth
                            disableElevation
                            onClick={() => navigate(MARKET_PLACE_BUY)}
                            color="secondary"
                        >
                            {i18n("dialog.trial_membership.become_trial.shareholder_cta")}
                        </CustomButton>
                    }
                />
            )}
        </>


    ];

    return (
        <>
            {introduceMembership ? (
                <ActionCardsDialog
                    open={isOpen}
                    handleClose={onClose}
                    disableBackdropClick={false}
                    hasCloseButton
                    maxWidth="md"
                    logo={
                        <HapimagLogo />
                    }
                    title={i18n("view.resort.is_waiting_for_you")}
                    description={
                        <Box
                            mt={2}
                            mb={5}
                        >
                            <Typography
                                variant={"body1"}
                                align={"center"}
                            >
                                {prospectMember ? i18n("dialog.trial_membership.become_trial.description") : i18n("view.booking.continue_login_or_trial_register")}
                            </Typography>
                        </Box>
                    }
                    cards={!authenticated ? loginOrTrialRegisterCards : (prospectMember ? membershipCards : null)}
                    alertClassName={classes.alert}
                    displayError={errorConvertTrial}
                    errorComponent={
                        <Typography
                            variant="body1"
                            color="textSecondary"
                        >
                            <b>{i18n("common.label.error_message") + ". "}</b>{i18n("common.label.call_support_center") + "."}
                        </Typography>
                    }
                />
            ) : (
                <ResponsiveDialog
                    open={isOpen}
                    handleClose={onClose}
                    disableBackdropClick={false}
                    hasCloseButton
                    maxWidth="sm"
                    content={(
                        <Box
                            display="flex"
                            alignItems="center"
                            flexDirection="column"
                            textAlign="center"
                        >
                            <img
                                src="/images/log-in-to-finalise-reservation.svg"
                                alt="already_registered"
                                width={100}
                                height={100}
                            />
                            <Box
                                mt={3}
                                mb={8}
                            >
                                <Typography
                                    variant="h3"
                                >
                                    {i18n("common.label.already_registered")}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                />
            )}
        </>
    );
};

LoginTrialOrMemberDialog.defaultProps = {
    showFullMembershipCardProspect: true
};

LoginTrialOrMemberDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    showFullMembershipCardProspect: PropTypes.bool
};


export default LoginTrialOrMemberDialog;
